/**
 * AI API-клиент (M19–M21).
 *
 * SSE-эндпоинты обрабатываются вручную через fetch + ReadableStream,
 * не через apiRequest (который ожидает JSON).
 */

import type {
  AiChatRequest, AiSseEvent, AiSuggestKind, AiJiraDescriptionResponse,
  AiConversationSummary, AiConversationDetail,
} from '@lens/shared';
import { apiRequest, getCsrfToken, resetCsrfToken } from '../../shared/api/client.ts';

// ─── SSE streaming helper ─────────────────────────────────────────────
//
// CSRF-токен берём из общего кэша shared/api/client.ts, а НЕ держим свой:
// раньше локальный `cachedCsrf` не сбрасывался никогда, и после ротации
// токена сервером (logout/refresh/sliding regeneration) все AI-запросы
// падали 403 до полной перезагрузки страницы. Теперь на 403 сбрасываем кэш
// и повторяем запрос один раз — как это делает apiRequest.

export interface SseCallbacks {
  onMeta?: (meta: {
    contextSize: number;
    contextTotal?: number;
    truncated?: boolean;
    scopeKind?: 'general' | 'view' | 'item';
    preset?: AiChatRequest['preset'] | null;
    decompositionApplied?: boolean;
    decompositionSteps?: number;
  }) => void;
  onDelta?: (text: string) => void;
  onDone?: (usage: { promptTokens: number; completionTokens: number }, conversationId?: string) => void;
  onError?: (code: string, message?: string) => void;
  onToolCall?: (toolCallId: string, name: string, args: Record<string, unknown>) => void;
  onToolResult?: (toolCallId: string, name: string, success: boolean, summary: string) => void;
  /** P2.3 — промежуточный прогресс bulk-операции. Вызывается многократно между onToolCall и onToolResult. */
  onToolProgress?: (toolCallId: string, name: string, message: string, current: number, total: number) => void;
}

async function fetchSse(
  url: string,
  body: unknown,
  callbacks: SseCallbacks,
  signal?: AbortSignal,
): Promise<void> {
  const doFetch = async (token: string): Promise<Response> =>
    fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': token,
      },
      body: JSON.stringify(body),
      credentials: 'include',
      signal,
    });

  let res = await doFetch(await getCsrfToken());
  // Токен протух (сервер ротировал) → сбрасываем кэш и повторяем один раз.
  if (res.status === 403) {
    resetCsrfToken();
    res = await doFetch(await getCsrfToken());
  }

  if (!res.ok) {
    // Если сервер вернул JSON-ошибку (до SSE)
    const contentType = res.headers.get('content-type') ?? '';
    if (contentType.includes('application/json')) {
      const err = (await res.json()) as { error?: { code: string; message: string } };
      callbacks.onError?.(err?.error?.code ?? 'unknown', err?.error?.message);
      return;
    }
    callbacks.onError?.('unknown', `HTTP ${res.status}`);
    return;
  }

  const reader = res.body?.getReader();
  if (!reader) return;

  const decoder = new TextDecoder();
  let buffer = '';

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop() ?? '';

    for (const line of lines) {
      if (!line.startsWith('data: ')) continue;
      const json = line.slice(6);
      try {
        const event = JSON.parse(json) as AiSseEvent;
        switch (event.type) {
          case 'meta':
            callbacks.onMeta?.({
              contextSize: event.contextSize,
              contextTotal: event.contextTotal,
              truncated: event.truncated,
              scopeKind: event.scopeKind,
              preset: event.preset,
              decompositionApplied: event.decompositionApplied,
              decompositionSteps: event.decompositionSteps,
            });
            break;
          case 'delta':
            callbacks.onDelta?.(event.text);
            break;
          case 'done':
            callbacks.onDone?.(event.usage, (event as any).conversationId);
            break;
          case 'error':
            callbacks.onError?.(event.code, event.message);
            break;
          case 'tool_call':
            callbacks.onToolCall?.(event.toolCallId, event.name, event.arguments as Record<string, unknown>);
            break;
          case 'tool_result':
            callbacks.onToolResult?.(event.toolCallId, event.name, event.success, event.summary);
            break;
          case 'tool_progress':
            callbacks.onToolProgress?.(
              event.toolCallId,
              event.name,
              event.message,
              event.current,
              event.total,
            );
            break;
        }
      } catch {
        // ignore parse errors
      }
    }
  }
}

// ─── Public API ───────────────────────────────────────────────────────

export function streamChat(
  request: AiChatRequest,
  callbacks: SseCallbacks,
  signal?: AbortSignal,
): Promise<void> {
  return fetchSse('/api/ai/chat', request, callbacks, signal);
}

/** Список доступных LLM-моделей + текущая (для селектора в чате). */
export function fetchAiModels(): Promise<{ models: string[]; current: string | null }> {
  return apiRequest<{ models: string[]; current: string | null }>('/api/ai/models');
}

export function streamSuggest(
  itemId: string,
  kind: AiSuggestKind,
  callbacks: SseCallbacks,
  signal?: AbortSignal,
  opts?: { fieldSlug?: string; fieldName?: string },
): Promise<void> {
  const body: Record<string, unknown> = { kind };
  if (opts?.fieldSlug) body.fieldSlug = opts.fieldSlug;
  if (opts?.fieldName) body.fieldName = opts.fieldName;
  return fetchSse(`/api/ai/item/${itemId}/suggest`, body, callbacks, signal);
}

export function generateJiraDescription(
  itemId: string,
): Promise<AiJiraDescriptionResponse> {
  return apiRequest<AiJiraDescriptionResponse>(`/api/ai/item/${itemId}/jira-description`, {
    method: 'POST',
  });
}

// ─── Conversations API ────────────────────────────────────────────────

export function listConversations(): Promise<{ conversations: AiConversationSummary[] }> {
  return apiRequest('/api/ai/conversations');
}

export function getConversation(id: string): Promise<AiConversationDetail> {
  return apiRequest(`/api/ai/conversations/${id}`);
}

export function createConversation(data: {
  title?: string; spaceId?: string; viewId?: string;
}): Promise<{ id: string; title: string }> {
  return apiRequest('/api/ai/conversations', { method: 'POST', body: data });
}

export function renameConversation(id: string, title: string): Promise<{ id: string; title: string }> {
  return apiRequest(`/api/ai/conversations/${id}`, { method: 'PATCH', body: { title } });
}

export function deleteConversation(id: string): Promise<void> {
  return apiRequest(`/api/ai/conversations/${id}`, { method: 'DELETE' });
}

export function suggestFormula(
  description: string,
  fields: Array<{ slug: string; name: string; type: string; formula?: string }>,
  currentFormula?: string,
  history?: Array<{ role: 'user' | 'assistant'; content: string }>,
  initialFormula?: string,
): Promise<{ formula: string }> {
  return apiRequest<{ formula: string }>('/api/ai/suggest-formula', {
    method: 'POST',
    body: { description, fields, currentFormula, history, initialFormula },
  });
}

export function rewriteText(
  text: string,
  instruction: string,
  opts?: { fieldName?: string; isMarkdown?: boolean },
): Promise<{ text: string }> {
  return apiRequest<{ text: string }>('/api/ai/rewrite-text', {
    method: 'POST',
    body: { text, instruction, fieldName: opts?.fieldName, isMarkdown: opts?.isMarkdown },
  });
}

export function explainFormula(
  formula: string,
  fields: Array<{ slug: string; name: string; type: string; formula?: string }>,
): Promise<{ explanation: string }> {
  return apiRequest<{ explanation: string }>('/api/ai/explain-formula', {
    method: 'POST',
    body: { formula, fields },
  });
}
